import React, {
  useImperativeHandle, forwardRef, useEffect
} from 'react';
import { PropTypes } from 'prop-types';
import {
  Container, Grid, FormControl, Radio, RadioGroup, FormControlLabel, FormLabel
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Controller, useFormContext } from 'react-hook-form';
import FormTextField from '@seccon/core/components/FormTextField';
import FormDatePicker from '@seccon/core/components/FormDatePicker';
import FormDropDown from '@seccon/core/components/FormDropDown';
import formatPhoneField from '../formatPhoneField';

const ForeignTravelOverviewForm = forwardRef(
  ({ travelReason, modeOfTransportation }, ref) => {
    const {
      control, trigger, getValues, formState: { errors }, watch, setValue
    } = useFormContext({});
    const willUseForeignPassport = watch('foreignTravel.willUseForeignPassport');
    const showForeignPassport = willUseForeignPassport === 'yes';

    const onValidate = async () => {
      const fieldsToValidate = [
        'foreignTravel.reasonForTravel',
        'foreignTravel.natureOfTripInformation',
        'foreignTravel.departureDate',
        'foreignTravel.returnDate',
        'foreignTravel.willUseForeignPassport'
      ];

      let isValid;
      if (!showForeignPassport) {
        // Trigger validation for all fields except 'foreignPassportInformation'
        isValid = await trigger(fieldsToValidate);
      } else {
        // Trigger validation for all fields
        isValid = await trigger();
      }

      if (isValid) {
        console.log('Validation successful');
        // Handle successful validation logic here
      } else {
        console.log('Validation failed');
        // Handle validation failure logic here
      }

      return isValid;
    };

    useImperativeHandle(ref, () => ({
      validate: async () => {
        const isValid = await onValidate();
        return isValid;
      }
    }));

    // Clean up the foreign passport information field if willUseForeignPassport is set to 'no'
    useEffect(() => {
      if (!showForeignPassport) {
        setValue('foreignTravel.foreignPassportInformation', null);
      }
    }, [showForeignPassport]);

    return (
      <Container>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormDropDown
                  control={control}
                  formErrors={errors}
                  label='Reason for Travel'
                  formFieldId='foreignTravel.reasonForTravel'
                  required
                  options={travelReason.data}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='General Nature of Trip'
                  formFieldId='foreignTravel.natureOfTripInformation'
                  required
                  multiline
                  maxRows={4}
                />
              </Grid>
              <Grid item xs={6}>
                <FormDatePicker
                  control={control}
                  formErrors={errors}
                  label='Departure from US Date'
                  formFieldId='foreignTravel.departureDate'
                  required
                  validationOptions={{
                    validate: (value) => {
                      const returnDate = getValues('foreignTravel.returnDate');
                      return (
                        !returnDate || new Date(value) <= new Date(returnDate) || 'Departure Date must be before Return Date'
                      );
                    }
                  }}
                  views={['year', 'month', 'day']}
                />
              </Grid>
              <Grid item xs={6}>
                <FormDatePicker
                  control={control}
                  formErrors={errors}
                  label='Return Date'
                  formFieldId='foreignTravel.returnDate'
                  required
                  validationOptions={{
                    validate: (value) => {
                      const departureDate = getValues('foreignTravel.departureDate');
                      return (
                        !departureDate || new Date(value) >= new Date(departureDate) || 'Return Date must be after Departure Date'
                      );
                    }
                  }}
                  views={['year', 'month', 'day']}
                />
              </Grid>
              <Grid item xs={6}>
                <FormDropDown
                  control={control}
                  formErrors={errors}
                  label='Type of Transportation Back to US'
                  formFieldId='foreignTravel.transportationMethodBackToUs'
                  options={modeOfTransportation.data}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Transportation Back to US Information'
                  formFieldId='foreignTravel.transportationBackToUsInformation'
                  multiline
                  maxRows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Names and Relationships of US Citizen Traveling Companions'
                  formFieldId='foreignTravel.usTravelCompanionsInformation'
                  multiline
                  maxRows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Names and Associations of Foreign Traveling Companions'
                  formFieldId='foreignTravel.foreignNationalTravelCompanionsInformation'
                  multiline
                  maxRows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Planned Contacts with Foreign Governments, Companies, or Citizens and Reason for Contact'
                  formFieldId='foreignTravel.plannedContactWithForeignPartiesInformation'
                  multiline
                  maxRows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name='foreignTravel.willUseForeignPassport'
                  control={control}
                  rules={{ required: true, message: 'Will use foreign passport is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl error={Boolean(fieldState.error)}>
                      <FormLabel>Traveling on a Foreign Passport at any point in your trip? *</FormLabel>
                      <RadioGroup
                        row
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      >
                        <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                        <FormControlLabel value='no' control={<Radio />} label='No' />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              {showForeignPassport && (
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Foreign Passport Information (Country, Number, and Reason)'
                    formFieldId='foreignTravel.foreignPassportInformation'
                    multiline
                    required
                    maxRows={4}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Foreign Phone Number'
                  formFieldId='foreignTravel.foreignPhoneNumber'
                  onBlur={(e) => formatPhoneField('foreignTravel.foreignPhoneNumber', setValue, e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  control={control}
                  formErrors={errors}
                  label='Additional Information'
                  formFieldId='foreignTravel.additionalInformation'
                  multiline
                  maxRows={4}
                />
              </Grid>
            </Grid>
          </form>
        </LocalizationProvider>
      </Container>
    );
  }
);

ForeignTravelOverviewForm.propTypes = {
  travelReason: PropTypes.object.isRequired,
  modeOfTransportation: PropTypes.object.isRequired
};

export default ForeignTravelOverviewForm;
